// apps/client/src/pages/leads/edm/components/TemplateGallery.tsx
//
// Plan-v3 core Slice E1 (edm_content): start a campaign from a saved or
// starter template, or save the current content as a template. The library
// loads only when opened, so opening the editor makes no extra call.
// Templates are stored through saveTemplate, which runs the same save-time
// sanitising and merge lint as a campaign.

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { LayoutTemplate, Save, Trash2 } from 'lucide-react';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { Skeleton } from '../../../../components/common/SkeletonLoader';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmSaveTemplateInput, EdmTemplate } from '../../../../hooks/useEdmContent';

const PATH_LABEL: Record<string, string> = {
  fixed_layout: 'Branded layout',
  custom_html: 'HTML',
};

const kindOf = (t: EdmTemplate): string =>
  Array.isArray(t.blocks) && t.blocks.length > 0 ? 'Blocks' : PATH_LABEL[t.contentPath] ?? t.contentPath;

const TemplateGallery: React.FC<{
  listTemplates: () => Promise<{ templates: EdmTemplate[] }>;
  saveTemplate: (input: EdmSaveTemplateInput) => Promise<{ templateId: string }>;
  deleteTemplate: (templateId: string) => Promise<unknown>;
  /** The current content, as a template would store it. */
  currentContent: () => Omit<EdmSaveTemplateInput, 'name' | 'templateId'>;
  /** True when applying a template would replace something the user wrote. */
  hasContent: boolean;
  /** True when there is body content to save as a template. */
  canSaveTemplate: boolean;
  onApply: (t: EdmTemplate) => void;
  disabled?: boolean;
}> = ({ listTemplates, saveTemplate, deleteTemplate, currentContent, hasContent, canSaveTemplate, onApply, disabled }) => {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<EdmTemplate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState<string | null>(null);
  const [pending, setPending] = useState<EdmTemplate | null>(null);
  const [deleting, setDeleting] = useState<EdmTemplate | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTemplates();
      setTemplates(res.templates);
    } catch (err) {
      setError(edmErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && templates === null && !loading) void load();
  };

  const apply = (t: EdmTemplate) => {
    if (hasContent) setPending(t);
    else onApply(t);
  };

  const save = async () => {
    const name = (saveName ?? '').trim();
    if (!name) return;
    setSaving(true);
    try {
      await saveTemplate({ name, ...currentContent() });
      toast.success('Template saved');
      setSaveName(null);
      if (templates !== null) void load();
    } catch (err) {
      toast.error(edmErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t: EdmTemplate) => {
    try {
      await deleteTemplate(t.templateId);
      setTemplates((list) => (list ?? []).filter((x) => x.templateId !== t.templateId));
    } catch (err) {
      toast.error(edmErrorMessage(err));
    }
  };

  return (
    <div className="space-y-3" data-testid="edm-template-gallery">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={EDM_UI.secondaryButton} onClick={toggle} aria-expanded={open} disabled={disabled}>
          <LayoutTemplate className="h-4 w-4" /> {open ? 'Hide templates' : 'Start from a template'}
        </button>
        {saveName === null ? (
          <button type="button" className={EDM_UI.secondaryButton} disabled={disabled || !canSaveTemplate} onClick={() => setSaveName('')}>
            <Save className="h-4 w-4" /> Save as template
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="edm-template-name" className="sr-only">
              Template name
            </label>
            <input
              id="edm-template-name"
              className={`${EDM_UI.input} w-64`}
              maxLength={120}
              placeholder="Template name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
            />
            <button type="button" className={EDM_UI.primaryButton} disabled={saving || !saveName.trim()} onClick={() => void save()}>
              Save
            </button>
            <button type="button" className={EDM_UI.secondaryButton} disabled={saving} onClick={() => setSaveName(null)}>
              Cancel
            </button>
          </div>
        )}
      </div>
      {open && (
        <div className="rounded-md border border-gray-700 bg-gray-900/40 p-2">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            <div className="px-2 py-2 text-xs text-red-300">
              {error}{' '}
              <button type="button" className="underline" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : (templates?.length ?? 0) === 0 ? (
            <div className="px-2 py-2 text-xs text-gray-500">No templates yet. Save a campaign&apos;s content as a template to reuse it.</div>
          ) : (
            <ul className="divide-y divide-gray-800">
              {(templates ?? []).map((t) => (
                <li key={t.templateId} className="flex items-center justify-between gap-2 px-2 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-gray-100">{t.name}</div>
                    <div className="text-xs text-gray-500">
                      {kindOf(t)}
                      {t.starter ? ' · Starter' : ''}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button type="button" className={EDM_UI.secondaryButton} disabled={disabled} onClick={() => apply(t)}>
                      Use
                    </button>
                    {!t.starter && (
                      <button
                        type="button"
                        className="p-1 text-gray-400 hover:text-red-300"
                        aria-label={`Delete template ${t.name}`}
                        onClick={() => setDeleting(t)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={pending !== null}
        title="Replace this campaign's content?"
        message="The template's subject and content replace what you have written. Save first if you want to keep it as a template."
        confirmLabel="Use template"
        confirmVariant="primary"
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) onApply(pending);
          setPending(null);
        }}
      />
      <ConfirmDialog
        isOpen={deleting !== null}
        title="Delete this template?"
        message="Campaigns already made from it keep their content."
        confirmLabel="Delete template"
        confirmVariant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) void remove(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
};

export default TemplateGallery;
